import type {PropsWithChildren} from 'react'
import {useEffect, useState} from 'react'
import {
  AppBar,
  Box,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Stack,
  styled,
  Toolbar,
  Typography
} from '@mui/material'
import {AuthButton} from '@goschool/auth'
import {GoSchool} from '@goschool/routing'
import {Breadcrumb, Breadcrumbs, HomeBreadcrumb} from './Breadcrumbs'
import {Trans, useTranslation} from 'react-i18next'
import {useCustomizationConfig} from '@goschool/model'


export interface PageReference {
  title: string;
  href: string;
}

interface RootProps {
  fullScreen?: boolean;
  centered?: boolean;
  breadcrumbs?: PageReference[];
  title?: string | null;
}

declare const __APP_VERSION__: string | undefined
declare const __BACKEND_VERSION__: string | undefined
declare const __DEPLOYMENT_ID__: string | undefined

function normalizeBuildMeta(value: string | undefined): string | null {
  if (typeof value !== 'string') {
    return null
  }
  const normalizedValue = value.trim()
  if (normalizedValue.length === 0) {
    return null
  }
  return normalizedValue
}

const frontendVersion = normalizeBuildMeta(__APP_VERSION__)
const backendVersion = normalizeBuildMeta(__BACKEND_VERSION__)
const deploymentId = normalizeBuildMeta(__DEPLOYMENT_ID__)

export function PageBreadcrumbs({breadcrumbs}: { breadcrumbs: PageReference[] }) {
  return <Breadcrumbs separator="›">
    <HomeBreadcrumb/>
    {breadcrumbs.map(
      ({title, href}, index) => <Breadcrumb key={index} to={href}>{title}</Breadcrumb>
    )}
  </Breadcrumbs>
}

export function PageLayout({children, fullScreen, breadcrumbs, centered, title}: PropsWithChildren<RootProps>) {
  const {siteTitle} = useCustomizationConfig()
  const [aboutOpen, setAboutOpen] = useState(false)

  useEffect(() => {
    if (title != null) {
      document.title = title
    }
  }, [title])

  return <PageContainer
    maxWidth={false} disableGutters={true} fullScreen={fullScreen} centered={centered}>
    {
      breadcrumbs &&
        <GoSchoolAppBar position="static" elevation={0}>
          <Toolbar variant="dense" disableGutters={true}>
            <PageBreadcrumbs breadcrumbs={breadcrumbs ?? []}/>
          </Toolbar>
          <AuthButton
            renderMenuItems={(closeMenu) => (
              <MenuItem
                key="about"
                onClick={() => {
                  closeMenu()
                  setAboutOpen(true)
                }}
              >
                <Trans i18nKey="legal:about.open"/>
              </MenuItem>
            )}
          />
        </GoSchoolAppBar>
    }
    {
      fullScreen
        ? children
        : <ContentContainer>{children}</ContentContainer>
    }
    {
      !fullScreen && <Footer/>
    }
    <AboutDialog
      open={aboutOpen}
      onClose={() => setAboutOpen(false)}
      siteTitle={siteTitle}
      frontendVersion={frontendVersion}
      backendVersion={backendVersion}
      deploymentId={deploymentId}
    />
  </PageContainer>
}

function Footer() {
  const {siteIcon} = useCustomizationConfig()

  return <FooterContainer>
    <Container maxWidth="lg">
      <Stack direction="row" justifyContent="space-between" alignItems="stretch">
        <Stack gap={1}>
          <img src={siteIcon} alt="GoSchool.AI" style={{height: '30px', maxHeight: '30px', width: 'auto'}}/>
        </Stack>
        <Stack justifyContent="center">
          <Typography variant="body2">&copy; 2024 GoSchool.AI</Typography>
        </Stack>
        <Stack gap={0}>
          <FooterLink href={GoSchool.termsOfUse}>
            <Trans i18nKey="legal:termsOfUse"/>
          </FooterLink>
          <FooterLink href={GoSchool.privacyStatement}>
            <Trans i18nKey="legal:privacyPolicy"/>
          </FooterLink>
          <FooterLink href={GoSchool.dsa}>
            <Trans i18nKey="legal:dsa"/>
          </FooterLink>
        </Stack>
      </Stack>
    </Container>
  </FooterContainer>
}

interface AboutDialogProps {
  open: boolean;
  onClose: () => void;
  siteTitle: string;
  frontendVersion: string | null;
  backendVersion: string | null;
  deploymentId: string | null;
}

function AboutDialog({
  open,
  onClose,
  siteTitle,
  frontendVersion,
  backendVersion,
  deploymentId
}: AboutDialogProps) {
  const {t} = useTranslation('legal')
  const frontendVersionText = frontendVersion ?? t('about.development')
  const backendVersionText = backendVersion ?? t('about.notProvided')
  const deploymentIdText = deploymentId ?? t('about.notProvided')

  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>{t('about.title')}</DialogTitle>
    <DialogContent>
      <Stack spacing={1}>
        <Typography variant="body2"><strong>{t('about.application')}:</strong> {siteTitle}</Typography>
        <Typography variant="body2"><strong>{t('about.webVersion')}:</strong> {frontendVersionText}</Typography>
        <Typography variant="body2"><strong>{t('about.apiVersion')}:</strong> {backendVersionText}</Typography>
        <Typography variant="body2"><strong>{t('about.deploymentId')}:</strong> {deploymentIdText}</Typography>
      </Stack>
    </DialogContent>
  </Dialog>
}

const FooterLink = styled(Link, {
  name: 'FooterLink',
  slot: 'Root'
})(
  ({theme}) => ({
    textDecoration: 'none',
    ...theme.typography.caption
  })
)


const FooterContainer = styled(Box, {
  name: 'FooterContainer',
  slot: 'Root'
})(
  ({theme}) => ({
    backgroundColor: theme.palette.background.paper,
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    '& > MuiContainer-root': {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center'
    }
  })
)


export function ContentContainer({children}: PropsWithChildren) {
  return <ContentContainerStyle maxWidth="lg">{children}</ContentContainerStyle>
}

export const GoSchoolAppBar = styled(AppBar)(
  ({theme}) => ({
    flex: '0 0 auto',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  })
)

export const PageContainer = styled(Container, {
  name: 'ContentContainer',
  slot: 'Root',
  shouldForwardProp: (prop) => prop !== 'fullScreen' && prop !== 'centered'
})<RootProps>(
  ({theme, fullScreen, centered}) => {
    const commonStyles = {
      backgroundColor: theme.palette.background.default,
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1
    } as const

    if (fullScreen) {
      return {
        ...commonStyles,
        overflow: 'hidden',
        height: '100vh',
        justifyContent: centered ? 'center' : 'stretch'
      }
    } else {
      return {
        ...commonStyles,
        overflowX: 'hidden',
        overflowY: 'auto',
        justifyContent: 'stretch',
        minHeight: '100vh'
      }
    }
  }
)


const ContentContainerStyle = styled(Container, {
  name: 'ContentContainer',
  slot: 'Root'
})(
  ({theme}) => ({
    backgroundColor: theme.palette.background.default,
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    gap: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1
  })
)
